import { ptTime } from "@/lib/api";
import WhatIf from "@/components/WhatIf";
import NearMisses from "@/components/NearMisses";

const cents = (x) => (x == null ? "—" : `${x}¢`);

function Tally({ d }) {
  return (
    <div data-testid="skip-tally" className="rounded-lg border border-[var(--border-subtle)] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-mute)] mb-2">Skips by rule · today (Pacific)</div>
      <div data-testid="skip-tally-summary" className="font-mono-num text-xs text-[var(--text-dim)] mb-2">
        {d.total} triggers · {d.traded} traded · {d.total - d.traded} skipped
      </div>
      {d.tally.length === 0 ? <div className="text-xs text-[var(--text-mute)]">No skips today</div> : (
        <div className="space-y-1.5">
          {d.tally.map((t) => (
            <div key={t.rule} data-testid={`skip-tally-${t.rule.toLowerCase().replaceAll(" ", "-")}`} className="text-xs font-mono-num">
              <div className="flex justify-between"><span>{t.rule}</span><span>{t.count}</span></div>
              <div className="h-1 rounded bg-[var(--border-subtle)] mt-0.5">
                <div className="h-1 rounded bg-amber-400 transition-[width] duration-500" style={{ width: `${(100 * t.count) / d.tally[0].count}%` }} />
              </div>
            </div>))}
        </div>
      )}
    </div>
  );
}

const COLS = [
  ["Time (PT)", (r) => ptTime(r.ts)], ["Asset", (r) => r.asset], ["Move", (r) => `${r.delta > 0 ? "+" : "-"}$${Math.abs(r.delta)}`],
  ["Need", (r) => `±$${r.threshold}`], ["Lookback", (r) => `${r.window}s`], ["Side", (r) => (r.side || r.direction).toUpperCase()],
  ["Side ask", (r) => cents(r.ask_cents)], ["Bid", (r) => cents(r.bid_cents)],
];

function Replay({ rows }) {
  return (
    <div data-testid="trigger-replay" className="md:col-span-2 overflow-auto max-h-80 rounded-lg border border-[var(--border-subtle)]">
      <table className="w-full text-xs font-mono-num whitespace-nowrap">
        <thead className="sticky top-0 bg-[var(--bg,#0b0f14)] text-[10px] uppercase tracking-widest text-[var(--text-mute)] text-left">
          <tr>{COLS.map(([h]) => <th key={h} className="px-2 py-2">{h}</th>)}<th className="px-2 py-2">What happened</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={COLS.length + 1} className="px-2 py-3 text-[var(--text-mute)]">No triggers yet today</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`trigger-row-${r.id}`} className="border-t border-[var(--border-subtle)]">
              {COLS.map(([h, fn]) => <td key={h} className="px-2 py-1.5">{fn(r)}</td>)}
              <td data-testid={`trigger-outcome-${r.id}`} className={`px-2 py-1.5 ${r.outcome === "traded" ? "text-[var(--buy)]" : "text-amber-300"}`}>
                {r.outcome === "traded" ? "Traded" : `Skipped · ${r.reason}`}
              </td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export default function TriggerPanel({ d, config }) {
  if (!d) return null;
  return (
    <section data-testid="trigger-panel" className="space-y-2">
      <h2 className="font-head font-bold text-base md:text-lg">Today's triggers</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
        <Tally d={d} />
        <Replay rows={d.triggers} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
        <WhatIf rows={d.triggers} nearMisses={d.near_misses || []} config={config} />
        <div className="md:col-span-2"><NearMisses rows={d.near_misses || []} /></div>
      </div>
    </section>
  );
}
